type CompiledPathParam = { paramName: string; isOptional?: boolean }
/**
 * Used to match on some portion of a URL pathname.
 */
export type PathPattern<Path extends string = string> = {
	/**
	 * A string to match against a URL pathname. May contain `:id`-style segments
	 * to indicate placeholders for dynamic parameters. It May also end with `/*`
	 * to indicate matching the rest of the URL pathname.
	 */
	path: Path
	/**
	 * Should be `true` if the static portions of the `path` should be matched in
	 * the same case.
	 */
	caseSensitive?: boolean
	/**
	 * Should be `true` if this pattern should match the entire URL pathname.
	 */
	end?: boolean
}

/**
 * The parameters that were parsed from the URL path.
 */
export type Params<Key extends string = string> = Readonly<
	Record<Key, string | undefined>
>

/**
 * Contains info about how a {@link PathPattern} matched on a URL pathname.
 */
export type PathMatch<ParamKey extends string = string> = {
	/**
	 * The names and values of dynamic parameters in the URL.
	 */
	params: Params<ParamKey>
	/**
	 * The portion of the URL pathname that was matched.
	 */
	pathname: string
	/**
	 * The portion of the URL pathname that was matched before child routes.
	 */
	pathnameBase: string
	/**
	 * The pattern that was used to match.
	 */
	pattern: PathPattern
}

// prettier-ignore
// eslint-disable-next-line
export type PathParam<path extends string> =  string;

// Attempt to parse the given string segment. If it fails, then just return the
// plain string type as a default fallback. Otherwise, return the union of the
// parsed string literals that were referenced as dynamic segments in the route.
export type ParamParseKey<Segment extends string> =
	// if you could not find path params, fallback to `string`
	[PathParam<Segment>] extends [never] ? string : PathParam<Segment>

type Mutable<T> = {
	-readonly [P in keyof T]: T[P]
}
export function compilePath(
	path: string,
	caseSensitive = false,
	end = true
): [RegExp, CompiledPathParam[]] {
	const params: CompiledPathParam[] = []
	let regexpSource =
		'^' +
		path
			.replace(/\/*\*?$/, '') // Ignore trailing / and /*, we'll handle it below
			.replace(/^\/*/, '/') // Make sure it has a leading /
			.replace(/[\\.*+^${}|()[\]]/g, '\\$&') // Escape special regex chars
			.replace(
				/\/:([\w-]+)(\?)?/g,
				(
					match: string,
					paramName: string,
					isOptional: string | undefined,
					index: number,
					str: string
				) => {
					params.push({ paramName, isOptional: isOptional != null })

					if (isOptional) {
						const nextChar = str.charAt(index + match.length)
						if (nextChar && nextChar !== '/') {
							return '/([^\\/]*)'
						}

						return '(?:/([^\\/]*))?'
					}

					return '/([^\\/]+)'
				}
			) // Dynamic segment
			.replace(/\/([\w-]+)\?(\/|$)/g, '(/$1)?$2') // Optional static segment

	if (path.endsWith('*')) {
		params.push({ paramName: '*' })
		regexpSource +=
			path === '*' || path === '/*'
				? '(.*)$' // Already matched the initial /, just match the rest
				: '(?:\\/(.+)|\\/*)$' // Don't include the / in params["*"]
	} else if (end) {
		// When matching to the end, ignore trailing slashes
		regexpSource += '\\/*$'
	} else if (path !== '' && path !== '/') {
		// If our path is non-empty and contains anything beyond an initial slash,
		// then we have _some_ form of path in our regex, so we should expect to
		// match only if we find the end of this path segment.  Look for an optional
		// non-captured trailing slash (to match a portion of the URL) or the end
		// of the path (if we've matched to the end).  We used to do this with a
		// word boundary but that gives false positives on routes like
		// /user-preferences since `-` counts as a word boundary.
		regexpSource += '(?:(?=\\/|$))'
	} else {
		// Nothing to match for "" or "/"
	}

	const matcher = new RegExp(regexpSource, caseSensitive ? undefined : 'i')

	return [matcher, params]
}
export function matchPath<Path extends string>(
	pattern: PathPattern<Path> | Path,
	pathname: string
): PathMatch<ParamParseKey<Path>> | null {
	if (typeof pattern === 'string') {
		pattern = { path: pattern, caseSensitive: false, end: true }
	}

	const [matcher, compiledParams] = compilePath(
		pattern.path,
		pattern.caseSensitive,
		pattern.end
	)

	const match = pathname.match(matcher)
	if (!match) return null

	const matchedPathname = match[0]
	let pathnameBase = matchedPathname.replace(/(.)\/+$/, '$1')
	const captureGroups = match.slice(1)
	const params: Params = compiledParams.reduce<Mutable<Params>>(
		(memo, { paramName, isOptional }, index) => {
			// We need to compute the pathnameBase here using the raw splat value
			// instead of using params["*"] later because it will be decoded then
			if (paramName === '*') {
				const splatValue = captureGroups[index] || ''
				pathnameBase = matchedPathname
					.slice(0, matchedPathname.length - splatValue.length)
					.replace(/(.)\/+$/, '$1')
			}

			const value = captureGroups[index]
			if (isOptional && !value) {
				memo[paramName] = undefined
			} else {
				memo[paramName] = (value || '').replace(/%2F/g, '/')
			}

			return memo
		},
		{}
	)

	return {
		params,
		pathname: matchedPathname,
		pathnameBase,
		pattern,
	}
}
